import * as React from 'react'
import { useState, useCallback } from 'react'
import isNotEmpty from './validators/isNotEmpty'
import inputValidator from './validators/inputValidator'
import formatErrorMessage from './helpers/formatErrorMessage'
import PropTypes from 'prop-types'
import { FormContext } from './FormWrapper'
import IconInfo from '../../icons/IconInfo'

const InputField = ({
  name,
  label,
  details = '',
  required = false,
  onChange,
  form,
  inputClassName = '',
  className = '',
  type = 'text',
  rows = null,
  validator = isNotEmpty,
  onFocus,
  pattern = '',
  placeholder = '',
  onBlur,
  id,
}) => {
  const forceValidation = React.useContext(FormContext)
  const [focus, setFocus] = useState(false)
  const [pristine, setPristine] = useState(true)
  const [validation, setValidation] = useState({ isValid: true, message: '' })
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const error = !validation.isValid && !pristine

  React.useEffect(() => {
    setShowErrorMessage(error)
    if (!error) return
    const timeout = setTimeout(() => {
      setShowErrorMessage(false)
    }, 5000)
    return () => {
      clearTimeout(timeout)
    }
  }, [error, setShowErrorMessage])

  const onChangeHandler = useCallback(
    e => {
      setPristine(false)
      const validation = inputValidator(e.target.value, validator, required, true, false)
      setValidation(validation)
      e.target.valid = validation.isValid
      if (onChange) onChange(e)
    },
    [onChange, setPristine, setValidation, required, validator, setShowErrorMessage]
  )

  const onFocusHandler = useCallback(
    e => {
      setFocus(true)
      if (onFocus) onFocus(e)
    },
    [setFocus]
  )
  const onBlurHandler = useCallback(
    e => {
      setFocus(false)
      setPristine(false)
      setValidation(inputValidator(e.target.value, validator, required, false, false))
      setShowErrorMessage(true)
      if (onBlur) onBlur(e)
    },
    [setFocus, setPristine, required, validator, setValidation, setShowErrorMessage]
  )
  React.useEffect(() => {
    if (!forceValidation) {
      setPristine(true)
      return
    }
    setPristine(false)
    const validation = inputValidator(form[name], validator, required, true, false)
    setValidation(validation)
  }, [forceValidation, form[name], validator, required, setValidation, setPristine])

  const errorStyle = error ? ' border-red-500 ' : ''
  return (
    <div className={'w-full relative mt-4 ' + className}>
      {details && (
        <div className="group w-full cursor-help ">
          <div
            className={
              (!error && form[name] && !focus
                ? 'border-emerald-500 text-emerald-500'
                : 'border-gray-400 hover:border-gray-700 ') +
              ' absolute top-3 right-2 h-6 w-6 border-2 rounded-full   flex justify-center items-center duration-300 ease-in-out'
            }
          >
            {!error && form[name] && !focus ? (
              <b>&#x2713;</b>
            ) : (
              <IconInfo className="w-4 h-4 text-gray-400 group-hover:text-gray-700 duration-300 ease-in-out" />
            )}
          </div>
          <div className="group-hover:block hidden absolute top-16 -right-1 bg-gray-700 text-white p-3 rounded-md shadow-lg z-30">
            <div className="bg-gray-700 absolute -top-2 right-4 h-4 w-4 -rotate-45"></div>
            {details}
          </div>
        </div>
      )}
      <div
        className={
          'pointer-events-none h-4 absolute top-0 left-0 transform px-2 text-gray-500 duration-100 ease-out ' +
          (focus || (form && form[name]) ? '-translate-y-2 text-xs ' : 'translate-y-3')
        }
      >
        <label
          className={'bg-white px-2 ' + (error ? ' text-red-500' : '')}
          htmlFor={(id || '') + name}
        >
          {' '}
          <nobr>
            {label}: {required ? '*' : ''}
          </nobr>
        </label>
      </div>
      {rows ? (
        <textarea
          className={'w-full placeholder:text-white placeholder:italic focus:placeholder:text-stone-400 border px-2 autofill:bg-white' + errorStyle}
          id={(id || '') + name}
          name={name}
          rows={rows}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
          value={form[name]}
          placeholder={placeholder}
        />
      ) : (
        <input
          className={
            (details ? 'pr-10 ' : '') +
            'h-12 w-full border px-2 autofill:bg-white ' +
            errorStyle +
            (inputClassName ? ' ' + inputClassName : '')
          }
          type={type}
          id={(id || '') + name}
          name={name}
          value={form[name]}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
        />
      )}
      {error && showErrorMessage ? (
        <>
          {/* <div className="bg-white mr-14 border-2 border-red-500 z-40 py-1 px-2 shadow-md rounded-lg absolute text-sm text-red-700 -translate-y-1 -translate-x-2 w-max">
            <div className="absolute -top-[.45rem] left-5 w-3 h-3 z-50 border-t-2 border-l-2 bg-white border-red-500 rotate-45"></div> */}

          <div className="mr-14 bg-red-500 z-40 py-1 px-2 shadow-md rounded-lg absolute text-sm text-white -translate-y-1 -translate-x-2 w-max animate-fade-out animation-delay-4000">
            <div className="absolute -top-[.35rem] left-5 w-3 h-3 z-50 bg-red-500 rotate-45"></div>
            {formatErrorMessage(validation, label)}
          </div>
        </>
      ) : null}
    </div>
  )
}
InputField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  form: PropTypes.object,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  type: PropTypes.string,
  rows: PropTypes.number,
  validator: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  id: PropTypes.string,
  details: PropTypes.string,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
}

export default InputField
